import { Link, useLocation } from 'react-router-dom';
import { 
  Settings as SettingsIcon, 
  Building2, 
  Webhook, 
  Database,
  ChevronRight,
  Shield,
  Bell,
  Mail,
  Users,
  Key,
  Globe,
  Zap
} from 'lucide-react';

interface SettingItem {
  title: string;
  description: string;
  icon: React.ElementType;
  path: string;
  badge?: string;
  badgeColor?: string;
}

const settingsItems: SettingItem[] = [
  {
    title: 'Business Settings',
    description: 'Configure company information, branding, and business preferences',
    icon: Building2,
    path: '/settings/business'
  },
  {
    title: 'Webhook Settings',
    description: 'Manage webhook configurations and event notifications',
    icon: Webhook,
    path: '/settings/webhooks'
  },
  {
    title: 'Redis Cache Monitor',
    description: 'Monitor and manage Redis cache storage in real-time',
    icon: Database,
    path: '/settings/redis-cache',
    badge: 'New',
    badgeColor: 'bg-green-100 text-green-700'
  },
  {
    title: 'SCIM Configuration',
    description: 'Configure SCIM 2.0 for automated user provisioning',
    icon: Users,
    path: '/settings/scim-config'
  },
  {
    title: 'SSO Settings',
    description: 'Configure Single Sign-On providers and authentication',
    icon: Key,
    path: '/settings/sso'
  },
  {
    title: 'Email Templates',
    description: 'Customize email templates and notification settings',
    icon: Mail,
    path: '/settings/email-templates'
  },
  {
    title: 'API Configuration',
    description: 'Manage API keys, rate limits, and access controls',
    icon: Globe,
    path: '/settings/api-config'
  },
  {
    title: 'Security Settings',
    description: 'Configure security policies, password rules, and compliance',
    icon: Shield,
    path: '/settings/security'
  },
  {
    title: 'Notification Preferences',
    description: 'Manage system-wide notification settings and channels',
    icon: Bell,
    path: '/settings/notifications'
  },
  {
    title: 'Performance Settings',
    description: 'Optimize system performance and resource utilization',
    icon: Zap,
    path: '/settings/performance'
  }
];

export default function Settings() {
  const location = useLocation();
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2 text-gray-900">
          <SettingsIcon className="w-8 h-8" />
          Settings
        </h1>
        <p className="text-gray-600 mt-2">
          Manage your application configuration and preferences
        </p>
      </div>
      
      {/* Settings Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {settingsItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          const isImplemented = [
            '/settings/business',
            '/settings/webhooks',
            '/settings/redis-cache'
          ].includes(item.path);
          
          return (
            <Link
              key={item.path}
              to={isImplemented ? item.path : '#'}
              className={`
                relative group bg-white rounded-lg border p-6 
                transition-all duration-200
                ${isImplemented 
                  ? 'border-gray-200 hover:border-blue-300 hover:shadow-lg cursor-pointer' 
                  : 'border-gray-100 opacity-60 cursor-not-allowed'
                }
                ${isActive ? 'border-blue-500 shadow-md' : ''}
              `}
              onClick={(e) => {
                if (!isImplemented) {
                  e.preventDefault();
                }
              }}
            >
              {/* Badge */}
              {item.badge && (
                <span className={`absolute top-3 right-3 px-2 py-0.5 text-xs font-medium rounded-full ${item.badgeColor}`}>
                  {item.badge}
                </span>
              )}
              
              {/* Icon */}
              <div className={`
                inline-flex p-3 rounded-lg mb-4
                ${isActive 
                  ? 'bg-blue-100 text-blue-600' 
                  : 'bg-gray-50 text-gray-600 group-hover:bg-blue-50 group-hover:text-blue-600'
                }
                ${!isImplemented ? 'group-hover:bg-gray-50 group-hover:text-gray-400' : ''}
                transition-colors
              `}>
                <Icon className="w-6 h-6" />
              </div>
              
              {/* Content */}
              <h3 className={`
                text-lg font-semibold mb-2
                ${isActive ? 'text-blue-900' : 'text-gray-900'}
              `}>
                {item.title}
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                {item.description}
              </p>
              
              {/* Action */}
              <div className="flex items-center justify-between">
                <span className={`
                  text-sm font-medium
                  ${isImplemented 
                    ? isActive 
                      ? 'text-blue-600' 
                      : 'text-gray-500 group-hover:text-blue-600'
                    : 'text-gray-400'
                  }
                `}>
                  {isImplemented ? 'Configure' : 'Coming Soon'}
                </span>
                {isImplemented && (
                  <ChevronRight className={`
                    w-4 h-4 transition-transform
                    ${isActive 
                      ? 'text-blue-600 translate-x-1' 
                      : 'text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1'
                    }
                  `} />
                )}
              </div>
              
              {/* Not Implemented Overlay */}
              {!isImplemented && (
                <div className="absolute inset-0 bg-gray-50 bg-opacity-50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="bg-gray-800 text-white text-xs px-3 py-1 rounded-full">
                    Not Available Yet
                  </span>
                </div>
              )}
            </Link>
          );
        })}
      </div>
      
      {/* Quick Stats */}
      <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">System Status</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-blue-700">Active Webhooks</p>
            <p className="text-2xl font-bold text-blue-900">3</p>
          </div>
          <div>
            <p className="text-sm text-blue-700">Cache Size</p>
            <p className="text-2xl font-bold text-blue-900">45.2M</p>
          </div>
          <div>
            <p className="text-sm text-blue-700">API Calls Today</p>
            <p className="text-2xl font-bold text-blue-900">12.5K</p>
          </div>
          <div>
            <p className="text-sm text-blue-700">System Health</p>
            <p className="text-2xl font-bold text-green-600">Good</p>
          </div>
        </div>
      </div>
    </div>
  );
}